import Vue from 'vue';
import store from '../store';
import router from '../router';
import Toasted from 'vue-toasted';
import i18n from '@/i18n';
import { fireGoogleTagError } from '@/utils/google-tag-manager-helpers';

Vue.use(Toasted);

export function handleAllErrors (errorPrefix: string, error: any): void {
	// 500s
	if (typeof error === 'string') {
		Vue.toasted.show(errorPrefix + error, { type: 'error', position: 'top-center' }).goAway(5000);
	}

	if (error.response?.data?.message) {
		// Avoid DTO validation errors
		if (Array.isArray(error.response.data.message)) {
			Vue.toasted.show(errorPrefix, { type: 'error', position: 'top-center' }).goAway(5000);
		}
		else {
			Vue.toasted.show(errorPrefix + error.response.data.message, { type: 'error', position: 'top-center' }).goAway(5000);
		}
	}

	else {
		Vue.toasted.show(errorPrefix, { type: 'error', position: 'top-center' }).goAway(5000);
	}
}

/**
 * The query to carry over when redirecting after an expired token. During the very first
 * navigation the router has not confirmed a route yet, so currentRoute is still the initial one
 * with an empty query while the address bar already holds the one the user opened.
 *
 * @return {Record<string, string>}
 */
function getRedirectQuery(): Record<string, string> {
	if (Object.keys(router.currentRoute.query).length) {
		return router.currentRoute.query as Record<string, string>;
	}

	return Object.fromEntries(new URLSearchParams(window.location.search));
}

/**
 * Build the localized message for a Pizzabot refusal (code 1803), which can land on set-costs as well as on order creation.
 * Refusals without reasons (V1) fall back to the backend message
 *
 * @param {any} error
 * @return {string|null} The message to display, or null if this is not a refusal
 */
export function getPosRefusalMessage(error: any): string|null {
	const data: any = error?.response?.data;

	if (data?.code !== 1803) {
		return null;
	}

	const reasons: string[] = data.details?.reasons || [];

	if (!reasons.length) {
		return data.message || i18n.t('checkout.pizzabot.refusal.generic') as string;
	}

	// Unknown reasons use the generic copy, de-duplicated so the same reason only reads once
	const knownReasons: string[] = ['queue_full', 'insufficient_stock', 'not_ready', 'accessibility_unavailable'];
	const keys: string[] = reasons.map((reason: string) => knownReasons.includes(reason) ? reason : 'generic');
	return Array.from(new Set(keys)).map((key: string) => i18n.t(`checkout.pizzabot.refusal.${key}`)).join(' ');
}

/**
 * Check if the error is due to an invalid/expired user Salata token
 * If so log the user out and redirect to login page
 *
 * @param {any} error
 * @param {string} redirect - Where to redirect to after error handling and logout
 * This was needed for login as because on refresh restaurant and event data is lost which is needed for this type of login to work
 * Because authentication happens before any routes resolve, we never hit the restaurant index if authentication fails, so we never get the data we need
 * So we redirect to the restaurant index first, which fetches the needed data first then handles where to redirect to afterwards
 * @return {void}
 */
export function expiredTokenErrorHandler(error: any, redirect?: string): void {
	if (error.response?.data?.code === 201) {
		store.commit('auth/LOG_USER_OUT');
		store.commit('auth/LOG_OPERATOR_OUT');
		store.commit('cart/CLEAR_SESSION_CHECKOUT_INFO');
		store.commit('filters/CLEAR_SELECTED_FILTERS');
		router.replace(redirect || { path: '/login', query: getRedirectQuery() });
		fireGoogleTagError({ name: 'expiredTokenError', error });
	}
}